import { useEffect, useRef, useState } from 'react';
import { PLAYER } from '../game/controller';
import { useGame } from '../game/store';

const DRAFT_KEY = 'qoobd-notes-draft';
const NAME_KEY = 'qoobd-notes-name';
const MAX = 4000;

const read = (k: string): string => {
  try {
    return localStorage.getItem(k) ?? '';
  } catch {
    return '';
  }
};
const write = (k: string, v: string): void => {
  try {
    if (v) localStorage.setItem(k, v);
    else localStorage.removeItem(k);
  } catch {
    /* storage unavailable: the draft just isn't remembered */
  }
};

/** Context sent with each note so feedback can be matched to a device and a cube. */
function details() {
  const s = useGame.getState();
  const r = s.result;
  const touch = typeof window !== 'undefined' && (navigator.maxTouchPoints > 0 || 'ontouchstart' in window);
  return {
    tier: s.tier,
    lastMatch: r
      ? {
          outcome: r.winner === PLAYER ? `won (${r.reason})` : `lost (${r.reason})`,
          tier: r.tier,
          seed: r.seed,
          seconds: Math.round(r.elapsedMs / 1000),
          steps: r.steps,
        }
      : null,
    device: touch ? 'touch' : 'mouse/keyboard',
    screen: `${window.innerWidth}×${window.innerHeight} @${window.devicePixelRatio}x`,
    standalone: window.matchMedia('(display-mode: standalone)').matches,
    build: __BUILD__,
    ua: navigator.userAgent,
  };
}

type Status = { kind: 'idle' } | { kind: 'sending' } | { kind: 'sent' } | { kind: 'error'; text: string };

/** "Add notes" button plus the feedback form it opens. Notes go to the server and are compiled on /notes. */
export function AddNotes({ className = 'cta alt' }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button ref={opener} className={className} type="button" onClick={() => setOpen(true)}>
        Add notes
      </button>
      {open && (
        <NotesForm
          onClose={() => {
            setOpen(false);
            opener.current?.focus();
          }}
        />
      )}
    </>
  );
}

function NotesForm({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState(() => read(NAME_KEY));
  const [text, setText] = useState(() => read(DRAFT_KEY));
  const [trap, setTrap] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => area.current?.focus(), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => write(DRAFT_KEY, text), [text]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim().length < 3 || status.kind === 'sending') return;
    setStatus({ kind: 'sending' });
    write(NAME_KEY, name.trim());
    try {
      const res = await fetch('/api/notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), text: text.trim(), website: trap, meta: details() }),
      });
      if (res.status === 429) throw new Error('Too many notes in a short time. Wait a few minutes and try again.');
      if (!res.ok) throw new Error("Your note couldn't be saved just now. It's kept here, so try again in a moment.");
      setText('');
      write(DRAFT_KEY, '');
      setStatus({ kind: 'sent' });
    } catch (err) {
      const msg = err instanceof TypeError ? "You seem to be offline. Your note is kept here; send it when you're back online." : (err as Error).message;
      setStatus({ kind: 'error', text: msg });
    }
  };

  return (
    <div className="overlay notes-overlay" role="dialog" aria-modal="true" aria-labelledby="notes-title" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form className="card" onSubmit={submit}>
        <div className="eyebrow">Tester feedback</div>
        <h2 id="notes-title">Add notes</h2>
        {status.kind === 'sent' ? (
          <>
            <p>Thanks, your note is in. Add another any time, before or after a match.</p>
            <div className="row">
              <button className="cta" type="button" onClick={() => setStatus({ kind: 'idle' })}>
                Write another
              </button>
              <button className="cta alt" type="button" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="note">
              What felt good, what felt off, anything confusing or broken. Your device type and your last match (difficulty, seed, result) are
              attached so it can be replayed.
            </p>
            <label className="field">
              <span className="lbl">Your name (optional)</span>
              <input value={name} maxLength={60} autoComplete="nickname" onChange={(e) => setName(e.target.value)} />
            </label>
            <div className="field">
              <label className="lbl" htmlFor="notes-text">
                Notes
              </label>
              <textarea
                id="notes-text"
                ref={area}
                value={text}
                maxLength={MAX}
                rows={7}
                required
                aria-describedby="notes-count"
                onChange={(e) => setText(e.target.value)}
              />
              <span className="count" id="notes-count">
                {text.length} / {MAX}
              </span>
            </div>
            <label className="trap" aria-hidden="true">
              Website
              <input tabIndex={-1} autoComplete="off" value={trap} onChange={(e) => setTrap(e.target.value)} />
            </label>
            {status.kind === 'error' && (
              <p className="note err" role="alert">
                {status.text}
              </p>
            )}
            <div className="row">
              <button className="cta" type="submit" disabled={status.kind === 'sending' || text.trim().length < 3}>
                {status.kind === 'sending' ? 'Sending…' : 'Send notes'}
              </button>
              <button className="cta alt" type="button" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
